import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../state/appStore';
import { useProgressStore } from '../../state/progressStore';
import { SimBridge } from '../../state/simBridge';
import { getLevel } from '../../levels';
import type { ObjectiveDef } from '../../levels/types';
import { Evaluator } from '../../levels/Evaluator';
import { getCar } from '../../sim/cars';
import { InputManager } from '../../controls/InputManager';
import { TouchSource } from '../../controls/TouchSource';
import { EngineApp } from '../../engine/EngineApp';
import { EngineAudio } from '../../audio/EngineAudio';
import { useT } from '../hooks';
import { Hud } from './Hud';

function zoneForObjective(def: ObjectiveDef | undefined): string | null {
  if (!def) return null;
  switch (def.type) {
    case 'full_stop':
      return def.zoneId ?? null;
    case 'drive_to_zone':
    case 'reverse_to_zone':
      return def.zoneId;
    case 'follow_path':
      return def.toZoneId;
    default:
      return null;
  }
}

export interface GameControls {
  togglePause: () => void;
  restart: () => void;
  exit: () => void;
}

export function GameScreen() {
  const t = useT();
  const levelId = useAppStore((s) => s.activeLevelId);
  const selectedCarId = useAppStore((s) => s.selectedCarId);
  const settings = useAppStore((s) => s.settings);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<EngineApp | null>(null);
  const [bridge] = useState(() => new SimBridge());
  const [touchSource] = useState(() => new TouchSource());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!levelId || !canvasRef.current) return;
    const level = getLevel(levelId);
    const spec = getCar(level.freeRoam && selectedCarId ? selectedCarId : level.car);
    const manager = new InputManager(
      { mode: spec.manual ? 'manual' : 'auto', gearCount: spec.gearRatios.length },
      undefined,
      touchSource,
    );
    const evaluator = new Evaluator(level);
    const audio = new EngineAudio(
      settings.audio,
      spec.regenBraking === true,
      spec.manual?.redlineRpm ?? 6300,
    );
    audio.attachGestureUnlock();
    let disposed = false;
    let finishing = false;
    let finishAtTick = 0;
    let cameraMode = settings.camera;
    bridge.setCamera(cameraMode);

    manager.onUiAction = (a) => {
      const engine = engineRef.current;
      if (!engine) return;
      if (a.type === 'camera') {
        cameraMode = cameraMode === 'chase' ? 'cockpit' : 'chase';
        engine.setCameraMode(cameraMode);
        bridge.setCamera(cameraMode);
      } else if (a.type === 'pause') {
        engine.paused = !engine.paused;
        bridge.setPaused(engine.paused);
      }
    };

    void EngineApp.create({
      canvas: canvasRef.current,
      level,
      spec,
      inputManager: manager,
      cameraMode,
      onTick: (snap, events) => {
        manager.setSpeedFeedback(snap.speedMs);
        const warnKeys: string[] = [];
        for (const ev of events) {
          if (ev.type === 'gearDenied') {
            warnKeys.push(`deny.${ev.reason}`);
            manager.syncGear(snap.gear);
          } else if (ev.type === 'signalCancelled') {
            manager.cancelSignal(ev.side);
          } else if (ev.type === 'crankBlocked') {
            warnKeys.push('warn.crankBlocked');
          }
        }
        const evalWarns = level.freeRoam ? [] : evaluator.tickUpdate(snap, events);
        const evalUi = level.freeRoam ? null : evaluator.uiState(snap);
        bridge.push(snap, evalUi, [...warnKeys, ...evalWarns]);
        audio.update(snap, events);

        if (evalUi) {
          const active = evalUi.objectives[evalUi.activeIndex];
          const def = active ? level.objectives.find((o) => o.id === active.id) : undefined;
          engineRef.current?.setActiveZone(zoneForObjective(def));
        }
        if (!level.freeRoam && evaluator.finished && !finishing) {
          finishing = true;
          finishAtTick = snap.tick + 75; // ~1,2 s para asimilar el final
        }
        if (finishing && snap.tick >= finishAtTick) {
          finishing = false;
          const result = evaluator.finish();
          const outcome = useProgressStore.getState().recordAttempt(result);
          useAppStore.getState().showDebrief({ result, outcome });
        }
      },
    }).then((engine) => {
      if (disposed) {
        engine.dispose();
        return;
      }
      engineRef.current = engine;
      engine.start();
      setLoading(false);
    });

    return () => {
      disposed = true;
      engineRef.current?.dispose();
      engineRef.current = null;
      manager.dispose();
      audio.dispose();
    };
    // settings solo se lee al montar: reiniciar nivel aplica cambios
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, selectedCarId, bridge, touchSource]);

  if (!levelId) return null;
  const level = getLevel(levelId);
  const hudSpec = getCar(level.freeRoam && selectedCarId ? selectedCarId : level.car);

  const controls: GameControls = {
    togglePause: () => {
      const engine = engineRef.current;
      if (!engine) return;
      engine.paused = !engine.paused;
      bridge.setPaused(engine.paused);
    },
    restart: () => useAppStore.getState().restartLevel(),
    exit: () => useAppStore.getState().exitToMenu(),
  };

  return (
    <div className="game-shell relative h-full w-full overflow-hidden bg-slate-950">
      <canvas ref={canvasRef} className="block h-full w-full" />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-950 text-lg text-white/70">
          {t('common.loading')}
        </div>
      )}
      <Hud bridge={bridge} spec={hudSpec} controls={controls} touchSource={touchSource} />
    </div>
  );
}
